import { useState } from "react";
import { MessageSquare, MessageCircle, MapPin, Phone, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { submitContact } from "@/lib/api";
import { BUSINESS } from "@/lib/constants";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Message sent. We’ll get back to you as soon as we can.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn’t send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <SEO
        title="Contact Carfornia Buyers — Hayward, CA"
        description="Questions about selling your car? Call or text Carfornia Buyers at (650) 339-6211 or send us a message. Veteran-owned, based in Hayward, CA."
        canonical="https://carforniabuyers.com/contact"
      />
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mt-3">
            Talk to the owner.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
            We are a small, veteran-owned business in Hayward. Call, text or send a message and we will get back to you as soon as we can.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            {[
              { Icon: Phone, t: "Call", v: BUSINESS.phone, href: BUSINESS.phoneHref, sub: "If we miss you, we return every call", testId: "contact-phone-card" },
              { Icon: MessageCircle, t: "Text", v: BUSINESS.phone, href: BUSINESS.smsHref, sub: "Text us your year, make, model and mileage", testId: "contact-sms-card" },
              { Icon: MapPin, t: "Location", v: BUSINESS.address, sub: "Please call before visiting", testId: "contact-address-card" },
              { Icon: MessageSquare, t: "Message", v: "Use the form on this page", sub: "Goes straight to the owner’s inbox", testId: "contact-form-card" },
            ].map(({ Icon, t, v, href, sub, testId }) => (
              <a key={t} href={href} data-testid={testId} className="flex items-start gap-4 p-6 rounded-2xl bg-brand-surface border border-slate-100 hover:border-brand-blue transition-colors">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue"><Icon className="w-5 h-5" /></span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t}</div>
                  <div className="font-display text-lg font-bold text-slate-900 mt-0.5">{v}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{sub}</div>
                </div>
              </a>
            ))}
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-100 rounded-3xl p-7 sm:p-9 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)]">
              {done ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <h2 className="font-display text-3xl font-extrabold text-slate-900">Thanks — we got it.</h2>
                  <p className="text-slate-600 mt-3">Your message went straight to the owner. We’ll reply as soon as we can. If it’s urgent, call or text <a href={BUSINESS.phoneHref} className="text-brand-blue font-semibold">{BUSINESS.phone}</a>.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Send us a message</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FieldC label="Your name *">
                      <Input data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12" placeholder="Jane Doe" />
                    </FieldC>
                    <FieldC label="Email *">
                      <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12" placeholder="you@email.com" />
                    </FieldC>
                    <FieldC label="Phone">
                      <Input data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12" placeholder="(555) 555-1234" />
                    </FieldC>
                    <FieldC label="Subject">
                      <Input data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="h-12" placeholder="How can we help?" />
                    </FieldC>
                  </div>
                  <FieldC label="Message *">
                    <Textarea data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} className="min-h-[160px]" placeholder="Tell us about your car or question…" />
                  </FieldC>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="w-full h-13 bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold rounded-xl shadow-lg shadow-blue-500/30"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send message</>}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldC({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
